import React, { useState, useEffect } from "react";
import FinancingRequestStudentData from "../../../models/financing-request/student-data/FinancingRequestStudentData";
import StepHandler from "../../../models/shared/StepHandler";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTable from "../../shared/controls/TrTable";
import { Paper, Typography, Grid, Button, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { hrDate, numberFormat, currencyFormat } from "../../../utilities/utilities";

import { AuthorizedCarrier } from "../../../models/financing-request/AuthorizedCarrier";
import { SchoolCarrier } from "../../../models/financing-request/SchoolCarrier";
import UploadModal from "../../../components/documents/documents-modal/UploadModal";
import FinancingRequestApiClient from "../../../api/FinancingRequestApiClient";
import DecisionDocument from "../../../models/financing-request/DecisionDocument";
import DocumentApiClient from "../../../api/DocumentApiClient";
import { Link } from "react-router-dom";
import { PdfDocument } from "../../../models/financing-request/PdfDocument";
import NoteModal from "../../shared/common/NoteModal";
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import Attachment from "@material-ui/icons/Attachment";

interface Props extends StepHandler {
    financingRequestId: number;
    handleSetShowWarningMessage: (showModal: boolean) => void;
    handleUpdateDocuments:  (
        decisionDocument: DecisionDocument,
        schoolCarriersData: SchoolCarrier[],
        authorizedCarriersData: AuthorizedCarrier[],
        studentsData: FinancingRequestStudentData[]
      ) => void,
    canLoadDocumentPage:boolean
}

interface StudentsDataStudent {
    id: number | undefined;
    studentId: number;
    name: string;
    birthDate: Date;
    oib: string;
    schoolClass: string;
    sectionMark: string;
    sectionTypeName: string;
    programName: string;
    transportOption: string;
    transportOptionName: string;
    uploadedDocuments?: PdfDocument[];
    note?: string;
}

const getVehicleTypeName = (type: number | null): string => {
    if (type === 1) {
        return "Autobus";
    }
    else if (type === 2) {
        return "Kombi";
    }
    else if (type === 3) {
        return "Školski automobil";
    } else {
        return "Unknown";
    }
};

export default function FinancingRequestDataSubmissionStep({
    buttons,
    onPrevious,
    onNext,
    goToPreviousStep,
    goToNextStep,
    onStepperSave,
    financingRequestId,
    handleSetShowWarningMessage,
    handleUpdateDocuments,
    canLoadDocumentPage
}: Props) {

    const [showData, setShowData] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUploadId, setCurrentUploadId] = useState<number | null>(null);
    const [alreadyUploadedDocumentCount, setAlreadyUploadedDocumentCount] = useState<number>(0);
    const [currentType, setCurrentType] = useState<'decision' | 'carrier' | 'student' | 'authorized'>('decision');
    const [decisionTableData, setDecisionTableData] = useState<DecisionDocument[] | undefined>();
    const [schoolCarriersTableData, setSchoolCarriersTableData] = useState<SchoolCarrier[] | undefined>();
    const [authorizedCarriersTableData, setAuthorizedCarriersTableData] = useState<AuthorizedCarrier[] | undefined>();
    const [studentsTableData, setStudentsTableData] = useState<StudentsDataStudent[] | undefined>();
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<string>("");

    const financingRequestApiClient = new FinancingRequestApiClient();
    const documentApiClient = new DocumentApiClient();

    const openNoteModal = (note: string) => {
        setSelectedNote(note);
        setIsNoteModalOpen(true); // Ensure this changes to true
    };
    
    const handleNoteGetInitialValue = () => {
        return selectedNote;
    }

    const closeNoteModal = () => {
        setIsNoteModalOpen(false);
    };

    const handleUploadDocuments = (
        id: number,
        files: FileList | null,
        type: 'decision' | 'carrier' | 'student' | 'authorized'
    ) => {

        if (files) {
            const newFiles = Array.from(files);

            if (newFiles.some(file => file.type !== 'application/pdf')) {
                alert('Dopušten je samo dokument u .pdf formatu');
                return;
            }
        }
    };

    
    useEffect(() => {
        if (canLoadDocumentPage) {
            loadPageData()
            .catch(console.error);
        }
    }, [canLoadDocumentPage]);

    //TODO QUICK FIX
    const traslateTransportOption = (transportOptionType: string) => {
        switch (transportOptionType)
        {
            case "1":
                return "Školski prijevoz";
            case "2":
                return "Osobni prijevoz";
            case "3":
                return "Osobni prijevoz putnik";
            case "4":
                return "Ovlašteni prijevoznik";
            case "5":
                return "Pratitelj u javnom prijevozu";
            default:
                return "";
        }
    }

    const loadPageData = async () => {
        var financingRequestData = await financingRequestApiClient.getFinancingRequestForEdit(financingRequestId);
        //let financingRequestData = await financingRequestApiClient.getFinancingRequestDataSubmissionByFinancingRequestId(financingRequestId);
        
        let decisionDocument = financingRequestData.decisionDocument;
        var decisionDocumentArray:DecisionDocument[] = [];
        if(decisionDocument){
            decisionDocumentArray.push(decisionDocument);
        }
        setDecisionTableData(decisionDocumentArray);

        setSchoolCarriersTableData(financingRequestData.schoolCarriers);

        setAuthorizedCarriersTableData(financingRequestData.authorizedCarriers);

        let studentsDataStudents = new Array<StudentsDataStudent>();
        financingRequestData.studentsData.forEach(studentData => {
            studentsDataStudents.push({
                id: studentData.id,
                studentId: studentData.student.id,
                name: studentData.student.lastName + " " + studentData.student.firstName,
                birthDate: studentData.student.birthDate,
                oib: studentData.student.oib,
                schoolClass: studentData.student.schoolClass,
                sectionMark: studentData.student.sectionMark,
                sectionTypeName: studentData.student.sectionTypeName,
                programName: studentData.student.programName,
                transportOption: studentData.transportOption && studentData.transportOption.transportOptionType ? studentData.transportOption.transportOptionType.toString() : "0",
                transportOptionName: traslateTransportOption(studentData.transportOption && studentData.transportOption.transportOptionType ? studentData.transportOption.transportOptionType.toString() : "0"),
                uploadedDocuments: studentData.student.uploadedDocuments,
                note: studentData.student.note
            });
        });
        setStudentsTableData(studentsDataStudents);

        handleUpdateDocuments(decisionDocument, financingRequestData.schoolCarriers, financingRequestData.authorizedCarriers, financingRequestData.studentsData);
        setShowData(true);
    }

    const handleUploadClick = (id: number, type: 'decision' | 'carrier' | 'student' | 'authorized', uploadedDocumentCount:number) => {
        setCurrentUploadId(id);
        setCurrentType(type);
        setAlreadyUploadedDocumentCount(uploadedDocumentCount)
        setIsModalOpen(true);
    };

    const handleDownloadDocumentClick = async (id: number, type: 'decision' | 'carrier' | 'student' | 'authorized', fileName: string) => {
        handleSetShowWarningMessage(false);
        if (type === 'decision') {
            let response = await documentApiClient.downloadDecisionPdf(id, fileName);
        } else if (type === 'carrier') {
            let response = await documentApiClient.downloadSchoolCarrierPdf(id, fileName);
        } else if (type === 'student') {
            let response = await documentApiClient.downloadStudentPdf(id, fileName);
        } else if (type === 'authorized') {
            let response = await documentApiClient.downloadAuthorizedCarrierPdf(id, fileName);
        }
        handleSetShowWarningMessage(true);
    };

    const handleDeleteDocument = async (id: number, type: 'decision' | 'carrier' | 'student' | 'authorized') => {
        if (type === 'decision') {
            let response = await documentApiClient.deleteDecisionPdf(id);
            loadPageData();
        } else if (type === 'carrier') {
            let response = await documentApiClient.deleteSchoolCarrierPdf(id);
            loadPageData();
        } else if (type === 'student') {
            let response = await documentApiClient.deleteStudentPdf(id);
            loadPageData();
        } else if (type === 'authorized') {
            let response = await documentApiClient.deleteAuthorizedCarrierPdf(id);
            loadPageData();
        }
    };

    let decisionTableHeadMetadata: TrTableHeadMetadata<DecisionDocument>[];
    let emptyDecisionDocument:DecisionDocument[] = [{documentGuid:"", fileName:"", filePath:"", financingRequestId:0,id:-1,note:"",uploadedAt:new Date()}]

    decisionTableHeadMetadata = [
        {
            id: "fileName",
            label: "Naziv odluke",
            dataFunc: (d: DecisionDocument) => (
                <Typography variant="body2">Odluka o potrebi osiguravanja prilagođenog prijevoza</Typography>
            )
        },
        {
            id: "documentation",
            label: "Dokumentacija",
            dataFunc: (d: DecisionDocument) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>

                        <div key={d.id} style={{ display: 'flex', alignItems: 'center' }}>
                            {d.id && d.id > 0 &&
                            (
                                <>
                                <Link to={{}} onClick={() => handleDownloadDocumentClick(d.id || 0, "decision", d.fileName || "document.pdf")}>
                                    {d.fileName}
                                </Link>

                                <IconButton
                                    onClick={() => handleDeleteDocument(d.id || 0, 'decision')}
                                    size="small"
                                    style={{ marginLeft: '5px', color: 'red' }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                                </>
                            )}
                        </div>

                    </div>
                    {!d.id ||d.id < 1 && (
                        <Attachment style={{ cursor: 'pointer', color: '#044c92' }} onClick={() => handleUploadClick(d.id || 0, 'decision', 0)}>
                        </Attachment>
                    )}
                </div>
            )
        },
        {
            id: "note",
            label: "Napomena",
            dataFunc: (d: DecisionDocument) => (
                <div>
                    {d.note && (
                        <MailOutlineIcon
                            color={d.note ? "error" : "action"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openNoteModal(d.note || "")}
                        />
                    )}
                </div>
            )
        }
    ];

    let schoolCarrierTableHeadMetadata: TrTableHeadMetadata<SchoolCarrier>[];

    schoolCarrierTableHeadMetadata = [
        {
            id: "vehicleType",
            label: "Vrsta vozila",
            dataName: "vehicleType",
            dataFunc: (d: SchoolCarrier) => {

                return (
                    <>
                        <Typography variant="body2">{getVehicleTypeName(d.vehicleType)}</Typography>
                    </>
                );
            }
        },
        { id: "registrationPlate", label: "Registracijska oznaka", dataName: "registrationPlate" },
        { id: "dailyMileageTotal", label: "Ukupna dnevna kilometraža", dataName: "dailyMileageTotal" },
        { id: "numberOfStudents", label: "Broj učenika", dataName: "numberOfStudents" },
        {
            id: "documentation",
            label: "Dokumentacija",
            dataFunc: (d: SchoolCarrier) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>

                            <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "carrier", file.fileName || "document.pdf")}>
                                {file.fileName}
                            </Link>

                            <IconButton
                                onClick={() => handleDeleteDocument(file.id || 0, 'carrier')}
                                size="small"
                                style={{ marginLeft: '5px', color: 'red' }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                            </div>
                        ))}
                    </div>
                    {!d.uploadedDocuments || d.uploadedDocuments.length < 5 &&
                        <Attachment style={{ cursor: 'pointer', color: '#044c92' }} onClick={() => handleUploadClick(d.id || 0, 'carrier', d.uploadedDocuments ? d.uploadedDocuments.length : 0)}>
                        </Attachment>
                    }
                </div>
            )
        },
        {
            id: "note",
            label: "Napomena",
            dataFunc: (d: SchoolCarrier) => (
                <div>
                    {d.note && (
                        <MailOutlineIcon
                            color={d.note ? "error" : "action"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openNoteModal(d.note || "")}
                        />
                    )}
                </div>
            )
        }
    ];

    let authorizedCarrierTableHeadMetadata: TrTableHeadMetadata<AuthorizedCarrier>[];

    authorizedCarrierTableHeadMetadata = [
        { id: "name", label: "Naziv prijevoznika", dataName: "name" },
        { id: "dailyMileage", label: "Ukupna dnevna kilometraža", dataName: "dailyMileage" },
        { id: "dailyPrice", label: "Cijena po danu", dataName: "dailyPrice" },
        { id: "numberOfStudents", label: "Broj učenika", dataName: "numberOfStudents" },
        {
            id: "documentation",
            label: "Dokumentacija",
            dataFunc: (d: AuthorizedCarrier) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>

                            <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "authorized", file.fileName || "document.pdf")}>
                                {file.fileName}
                            </Link>

                            <IconButton
                                onClick={() => handleDeleteDocument(file.id || 0, 'authorized')}
                                size="small"
                                style={{ marginLeft: '5px', color: 'red' }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                            </div>
                        ))}
                    </div>
                    {!d.uploadedDocuments || d.uploadedDocuments.length < 5 &&
                        <Attachment style={{ cursor: 'pointer', color: '#044c92' }} onClick={() => handleUploadClick(d.id || 0, 'authorized', d.uploadedDocuments ? d.uploadedDocuments.length : 0)}>
                        </Attachment>
                    }
                </div>
            )
        },
        {
            id: "note",
            label: "Napomena",
            dataFunc: (d: AuthorizedCarrier) => (
                <div>
                    {d.note && (
                        <MailOutlineIcon
                            color={d.note ? "error" : "action"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openNoteModal(d.note || "")}
                        />
                    )}
                </div>
            )
        }
    ];

    let studentTableHeadMetadata: TrTableHeadMetadata<StudentsDataStudent>[];

    studentTableHeadMetadata = [
        {
            id: "studentName",
            label: "Učenik",
            dataName: "name",
            dataFunc: (d: StudentsDataStudent) => (
                <>
                    <Typography variant="body2">{d.name}</Typography>
                    <Typography variant="body2">{hrDate(d.birthDate)}</Typography>
                </>
            )
        },
        { id: "oib", label: "OIB", dataName: "oib" },
        {
            id: "schoolClass",
            label: "Razred",
            dataName: "schoolClass",
            dataFunc: (d: StudentsDataStudent) => (
                <>
                    <Typography variant="body2">{d.programName}</Typography>
                    <Typography variant="body2">
                        {d.schoolClass + " - " + d.sectionMark}
                    </Typography>
                </>
            )
        },
        // {
        //     id: "vrstaOdjeljenja",
        //     label: "Vrsta odjeljenja",
        //     dataFunc: (d: StudentsDataStudent) => (
        //         <Typography variant="body2">{d.sectionTypeName}</Typography>
        //     )
        // },
        { id: "transportOption", label: "Prijevoz", dataName: "transportOptionName" },
        {
            id: "documentation",
            label: "Dokumentacija",
            dataFunc: (d: StudentsDataStudent) => {

                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center' }}>

                                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "student", file.fileName || "document.pdf")}>
                                    {file.fileName}
                                </Link>
    
                                <IconButton
                                    onClick={() => handleDeleteDocument(file.id || 0, 'student')}
                                    size="small"
                                    style={{ marginLeft: '5px', color: 'red' }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                                </div>
                            ))}
                        </div>
                        {d.transportOption && (d.transportOption == '2' || d.transportOption == '5') && (!d.uploadedDocuments || d.uploadedDocuments.length < 3) && (
                            <Attachment style={{ cursor: 'pointer', color: '#044c92' }} onClick={() => handleUploadClick(d.id || 0, 'student', d.uploadedDocuments ? d.uploadedDocuments.length : 0)}>
                            </Attachment>
                        )}
                    </div>
                );
            }
        },
        {
            id: "note",
            label: "Napomena",
            dataFunc: (d: StudentsDataStudent) => (
                <div>
                    {d.note && (
                        <MailOutlineIcon
                            color={d.note ? "error" : "action"}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openNoteModal(d.note || "")}
                        />
                    )}
                </div>
            )
        }
    ];

    // Botuni
    const handlePreviousStep = () => {
        if (goToPreviousStep != null) {
            goToPreviousStep();
        }
    };

    const handleNextStep = () => {
        if (goToNextStep != null) {
            goToNextStep();
        }
    };

    const handleUpdateState = () => { };

    onPrevious && onPrevious(handlePreviousStep);
    onNext && onNext(handleNextStep);
    onStepperSave && onStepperSave(handleUpdateState);

    const handleModalClose = () => {
        setIsModalOpen(false);
        setCurrentUploadId(null);
        loadPageData()
            .catch(console.error);
    };

    return (
        <>
        {
            showData &&
            <Grid container spacing={4}>
                {decisionTableData && decisionTableData.length > 0 ? (
                    <Grid item lg={12}>
                        <Paper>
                            <TrTable
                                headMetadata={decisionTableHeadMetadata}
                                bodyData={{
                                    idDataName: "id",
                                    data: decisionTableData
                                }}
                            />
                        </Paper>
                    </Grid>
                ) : (
                    <Grid item lg={12}>
                        <Paper>
                            <TrTable
                                headMetadata={decisionTableHeadMetadata}
                                bodyData={{
                                    idDataName: "id",
                                    data: emptyDecisionDocument
                                }}
                            />
                        </Paper>
                    </Grid>
                )}

                {schoolCarriersTableData && schoolCarriersTableData.length > 0 ? (
                    <Grid item lg={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Školski prijevoznici
                        </Typography>
                        <Paper>
                            <TrTable
                                headMetadata={schoolCarrierTableHeadMetadata}
                                bodyData={{
                                    idDataName: "id",
                                    data: schoolCarriersTableData
                                }}
                            />
                        </Paper>
                    </Grid>
                ) : (
                    <></>
                )}

                {authorizedCarriersTableData && authorizedCarriersTableData.length > 0 ? (
                    <Grid item lg={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Ovlašteni prijevoznici
                        </Typography>
                        <Paper>
                            <TrTable
                                headMetadata={authorizedCarrierTableHeadMetadata}
                                bodyData={{
                                    idDataName: "id",
                                    data: authorizedCarriersTableData
                                }}
                            />
                        </Paper>
                    </Grid>
                ) : (
                    <></>
                )}
                {studentsTableData && studentsTableData.length > 0 ? (
                    <Grid item lg={12}>
                        <Paper>
                            <TrTable
                                headMetadata={studentTableHeadMetadata}
                                bodyData={{
                                    idDataName: "studentId",
                                    data: studentsTableData
                                }}
                            />
                        </Paper>
                    </Grid>
                ) : (
                    <></>
                )}

                
            </Grid>
            }
            {buttons}

            <UploadModal
                open={isModalOpen}
                handleClose={handleModalClose}
                handleUpload={(files) => handleUploadDocuments(currentUploadId!, files, currentType)}
                currentType={currentType}
                financingRequestId={financingRequestId}
                schoolCarrierId={currentUploadId}
                authorizedCarrierId={currentUploadId}
                financingRequestStudentDataId={currentUploadId}
                alreadyUploadedDocumentCount={alreadyUploadedDocumentCount}
            />

            <NoteModal
                    open={isNoteModalOpen}
                    handleClose={closeNoteModal}
                    handleSave={() => {}}
                    handleNoteGetInitialValue={handleNoteGetInitialValue}
                    isEditable={false}
                />
        </>
    );
}